const STORAGE_KEY = 'swile:native_handoff'

// Bounds how long a leftover marker (OS-killed tab, crash, shared device) can strand a
// brand-new sign-in behind the blocked screen: past this age the marker is treated as
// absent rather than trusted, regardless of what still-visible/hidden signals saw.
//
// Must stay strictly below SessionAccountConstants.param_expiration (10 minutes,
// app/controllers/concerns/login_context_concern.rb) — the marker stores the
// /oauth/authorize URL carrying that server-minted, time-limited marker, and a retry
// replaying it (see useNativeAppHandoff.retry, no-resolver branch) must never be able
// to outlive it.
export const NATIVE_HANDOFF_MARKER_MAX_AGE_MS = 5 * 60 * 1000

type StoredHandoffAttempt = {
  url: string
  ts: number
}

/**
 * Per-tab marker surviving a Custom Tab / auth-webview navigation that gets torn down.
 * Wrapped in try/catch: storage can throw when access is restricted (private mode,
 * storage-partitioning); the marker is best-effort and must fail open (no marker ⇒
 * behave exactly like an unblocked hop), never break the sign-in flow.
 */
export const markNativeHandoffAttempt = (url: string): void => {
  try {
    const entry: StoredHandoffAttempt = { url, ts: Date.now() }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch {
    // best-effort — see module docblock
  }
}

export const readNativeHandoffAttempt = (): string | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    let parsed: Partial<StoredHandoffAttempt>
    try {
      parsed = JSON.parse(raw) as Partial<StoredHandoffAttempt>
    } catch {
      // Malformed or legacy (pre-TTL, raw-string) entry — discard rather than trust it.
      window.sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    if (typeof parsed.url !== 'string' || typeof parsed.ts !== 'number') {
      window.sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (Date.now() - parsed.ts > NATIVE_HANDOFF_MARKER_MAX_AGE_MS) {
      window.sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed.url
  } catch {
    return null
  }
}

export const clearNativeHandoffAttempt = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // best-effort — see module docblock
  }
}
